/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderFormSchemaFactory, BuilderUserContext, FormRootSchema,
  FormState, FormStateSchema,
  whenAuRegion,
  whenCreateMode,
  whenCreateOrEditMode,
  whenEditOrViewMode,
  whenMasterZoneView,
  whenNzRegion, whenOfficeView,
  whenUsOrAuRegion,
  whenUsRegion,
} from '@gh/builder-shared';
import { OfficeStatus, UserType } from '@gh/builder-shared-data';
import { getEmailLocalName, isRouteForCreationMode, MaskedTypes } from '@gh/common-shared';
import { FormMode, getControlValue, setControlValue } from '@gh/core-ui';
import { SubOfficeDetails } from '../../resources/sub-office-details.model';

@Injectable()
export class SubOfficeDetailsFormState extends FormState<SubOfficeDetails> {
  constructor(
    private route: ActivatedRoute,
    schemaFactory: BuilderFormSchemaFactory,
    private userContext: BuilderUserContext,
  ) {
    super(schemaFactory);
  }

  get submitValue(): SubOfficeDetails {
    return this.group.getRawValue();
  }

  updateEmail(value: string): void {
    setControlValue(this.group, 'email', getEmailLocalName(value));
  }

  updateLicenseState(): void {
    this.get('licenseNo').updateSchema({
      enabled: getControlValue(this.group, 'applyLicense'),
    });
  }

  protected buildSchema(details: SubOfficeDetails, { root }: FormStateSchema): FormRootSchema {
    let mode: FormMode;
    if (isRouteForCreationMode(this.route)) {
      mode = FormMode.CreateMode;
    } else if (details.status === OfficeStatus.Deactivated || this.userContext.userType === UserType.Employee) {
      mode = FormMode.ViewMode;
    } else {
      mode = FormMode.EditMode;
    }

    return root({
      mode,
      enabled: whenCreateOrEditMode(),
      optimisticLock: true,
      children: {
        id: void 0,
        status: {
          visible: whenEditOrViewMode(),
        },
        officeId: {
          required: true,
          enabled: whenCreateMode(),
          visible: whenMasterZoneView() || whenOfficeView(),
        },
        companyName: {
          required: true,
        },
        displayName: {
          required: true,
        },
        taxId: {
          visible: whenUsRegion(),
          type: MaskedTypes.TaxId,
        },
        abn: {
          visible: whenAuRegion(),
          type: MaskedTypes.Abn,
        },
        acn: {
          visible: whenAuRegion(),
          type: MaskedTypes.Acn,
        },
        tradingAs: {
          required: true,
        },
        gst: {
          visible: whenNzRegion(),
          type: MaskedTypes.Gst,
        },
        street1: {
          required: true,
        },
        street2: void 0,
        city: {
          required: true,
        },
        state: {
          required: true,
          visible: whenUsOrAuRegion(),
        },
        postCode: {
          required: true,
          type: MaskedTypes.Zip,
        },
        applyLicense: void 0,
        isPublicListed: void 0,
        licenseNo: {
          enabled: details.applyLicense,
        },
        email: {
          enabled: false,
        },
        phoneOffice: void 0,
        fax: void 0,
        phoneMobile: void 0,
      },
    });
  }
}
